import { Outlet } from 'react-router';
import type { RouteObject } from 'react-router';
import { AppLayout } from '~/layouts/app-layout';
import { AuthLayout } from '~/layouts/auth-layout';
import { ErrorBoundary } from '~/components/error-boundary';
import { adminRoute, builderRoute } from '~/components/admin-only';

export const routes: RouteObject[] = [
  {
    element: <Outlet />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          {
            path: '/login',
            lazy: () => import('~/routes/login'),
          },
          {
            path: '/auth-error',
            lazy: () => import('~/routes/auth-error'),
          },
          {
            path: '/onboard',
            lazy: () => import('~/routes/onboard'),
          },
          // Clickable design preview of onboarding + day one (FRONT-904/905/
          // 907) — real components, zero backend writes. Dev builds only:
          // the route sits under the unauthenticated auth shell, so a prod
          // registration would expose internal design content publicly.
          ...(import.meta.env.DEV
            ? [
                {
                  path: '/onboard-preview',
                  lazy: () => import('~/routes/onboard-preview'),
                },
              ]
            : []),
          {
            // OAuth consent (FRONT-338): GET /api/oauth/authorize redirects
            // here. Centered-card shell, no app chrome — the page gates its
            // own session and sends the browser to the client's redirect URI.
            path: '/authorize',
            lazy: () => import('~/routes/authorize'),
          },
          {
            // Slack account linking (FRONT-2199): `/eddy link` in Slack hands
            // out a URL to this page. Same centered-card shell and self-gated
            // session as the consent page above.
            path: '/link/slack',
            lazy: () => import('~/routes/link-slack'),
          },
        ],
      },
      {
        // The phone surface (PWA). Outside AppLayout: that shell is a fixed
        // desktop three-column layout. This route runs its own auth/session
        // gates and owns the whole viewport. Phone-sized browsers are sent
        // here automatically (see lib/mobile-redirect).
        path: '/m',
        lazy: () => import('~/routes/mobile'),
      },
      {
        // FRONT-3164: one chat or Build in its own browser window. Outside
        // AppLayout — no sidebar, no top bar; it runs its own gates.
        path: '/popout/:kind/:id',
        lazy: () => import('~/routes/popout'),
      },
      {
        element: <AppLayout />,
        children: [
          {
            index: true,
            lazy: () => import('~/routes/home'),
          },
          {
            // Legacy alias — Home/Chat were merged (post-MP roadmap). Deep
            // links and old browser bookmarks land on the same Inbox surface.
            path: '/chat',
            lazy: () => import('~/routes/home'),
          },
          {
            path: '/chats',
            lazy: () => import('~/routes/chats'),
          },
          {
            path: '/memory',
            lazy: () => import('~/routes/memory'),
          },
          {
            // FRONT-892: the Work board — a curation lens over the graph's
            // project entities.
            path: '/work',
            lazy: () => import('~/routes/work'),
          },
          {
            // FRONT-971: one container's detail page — important people,
            // context/key facts + notes, and its manual tasks.
            path: '/work/:id',
            lazy: () => import('~/routes/work-detail'),
          },
          {
            path: '/reflect',
            lazy: () => import('~/routes/reflect'),
          },
          {
            path: '/files',
            lazy: () => import('~/routes/files'),
          },
          {
            // FRONT-1460: the section is called Knowledge now. The URL stays
            // /files (renaming it would touch 35 call sites for no visible
            // gain), so /knowledge is a redirect for anyone who types the
            // name they see in the sidebar.
            path: '/knowledge',
            lazy: async () => {
              const { Navigate } = await import('react-router');
              return { Component: () => <Navigate to="/files" replace /> };
            },
          },
          {
            path: '/admin/apps',
            lazy: () => import('~/routes/apps-admin'),
          },
          {
            // FRONT-2376: Sandbox settings moved into the System Settings
            // modal — this bare path is now a redirect. The modal itself is
            // the admin gate (see `system-settings-redirect.tsx`), so no
            // `adminRoute(...)` wrapper here any more.
            path: '/admin/sandbox',
            lazy: () => import('~/routes/system-settings-redirect'),
          },
          {
            // FRONT-2412: the live list is now the Sandbox section's own tab.
            path: '/admin/sandbox/pods',
            lazy: async () => {
              const { Navigate } = await import('react-router');
              return { Component: () => <Navigate to="/system-settings?section=sandbox&tab=live" replace /> };
            },
          },
          {
            path: '/admin/custom-agents/new',
            lazy: builderRoute(() => import('~/routes/custom-agent-new'), 'Custom Agent'),
          },
          {
            path: '/admin/custom-agents/:id/edit',
            lazy: builderRoute(() => import('~/routes/custom-agent-edit'), 'Custom Agent'),
          },
          {
            path: '/admin/apps/:slug',
            lazy: async () => {
              const { Navigate, useParams } = await import('react-router');
              return {
                Component: function AppsSlugRedirect() {
                  const { slug } = useParams<{ slug: string }>();
                  return <Navigate to={`/admin/apps/${slug}/edit`} replace />;
                },
              };
            },
          },
          {
            path: '/admin/apps/:slug/edit',
            lazy: () => import('~/routes/apps-edit'),
          },
          {
            // FRONT-3159: a draft in its sealed preview, full page (the author's Building Session opens it).
            path: '/admin/apps/:slug/drafts/:userId/preview',
            lazy: () => import('~/routes/apps-draft-preview'),
          },
          {
            // FRONT-1462: an app owner's per-app usage/cost view, opened
            // from the app's own row in the Apps admin list.
            path: '/admin/apps/:slug/analytics',
            lazy: () => import('~/routes/apps-analytics'),
          },
          {
            // FRONT-1501 originally put the Sketches gallery here as its own
            // page. FRONT-1549 folded it into a tab on the Knowledge page
            // instead (consolidating navigation), so this bare path is now a
            // redirect — mirroring the /knowledge → /files redirect above.
            // The backend still claims `/sketches/<id>` (a non-empty id
            // segment — see the dev proxy pattern in vite.config.ts); this
            // SPA route only ever claimed the bare path, so the backend's
            // route is untouched. When `eddy.sketches` is off, the Files
            // page's tab resolver falls back to the 'open' tab (see
            // `resolveFileListTab` in routes/files.tsx) and the user lands
            // there with `?tab=sketches` still sitting in the address bar —
            // that's left in place deliberately rather than stripped, since
            // the flag winning is the correct outcome and there's nothing to
            // reconcile.
            path: '/sketches',
            lazy: async () => {
              const { Navigate } = await import('react-router');
              return { Component: () => <Navigate to="/files?tab=sketches" replace /> };
            },
          },
          {
            path: '/files/new',
            lazy: () => import('~/routes/files'),
          },
          {
            path: '/files/:id',
            lazy: () => import('~/routes/files'),
          },
          {
            path: '/files/doc/:filename',
            lazy: () => import('~/routes/files'),
          },
          {
            // FRONT-776: a Files-screen folder — dispatched through the same
            // lazy module + `modeFromLocation` pattern as the other /files/*
            // routes, so a folder is deep-linkable and survives a reload.
            path: '/files/folder/:folderId',
            lazy: () => import('~/routes/files'),
          },
          {
            path: '/crons',
            lazy: () => import('~/routes/crons'),
          },
          {
            // FRONT-2439: the system reflection jobs as one schedule. A static
            // segment, so it outranks `/crons/:id` below.
            path: '/crons/reflection',
            lazy: () => import('~/routes/cron-reflection'),
          },
          {
            path: '/crons/:id',
            lazy: () => import('~/routes/cron-detail'),
          },
          {
            path: '/skills',
            lazy: () => import('~/routes/skills'),
          },
          {
            // FRONT-1201: the platform's workflow editor, rendered from the
            // shared @theblockbrain/workflow-ui package.
            path: '/workflows',
            lazy: () => import('~/routes/workflows'),
          },
          {
            // FRONT-3003: the person's own Claude Code in a terminal; dark
            // release behind the per-person `eddy.pro-builder` flag.
            path: '/pro-builder',
            lazy: () => import('~/routes/pro-builder'),
          },
          {
            path: '/workflows/:id',
            lazy: () => import('~/routes/workflow-detail'),
          },
          {
            // FRONT-2376: Tools moved into the System Settings modal.
            path: '/tools',
            lazy: () => import('~/routes/system-settings-redirect'),
          },
          {
            path: '/tools/new',
            lazy: () => import('~/routes/tool-new'),
          },
          {
            path: '/tools/:slug',
            lazy: () => import('~/routes/tool-detail'),
          },
          {
            // FRONT-2376: Connections moved into the System Settings modal.
            path: '/connections',
            lazy: () => import('~/routes/system-settings-redirect'),
          },
          {
            // Legacy alias (FRONT-985): the tabbed /integrations page was
            // replaced by /connections, which is itself a redirect now — sent
            // straight at the modal rather than chaining through /connections.
            path: '/integrations',
            lazy: async () => {
              const { Navigate } = await import('react-router');
              return {
                Component: function IntegrationsRedirect() {
                  return <Navigate to="/system-settings?section=connections" replace />;
                },
              };
            },
          },
          {
            path: '/agents',
            lazy: () => import('~/routes/agents'),
          },
          {
            // FRONT-2376: the Audit log moved into the System Settings modal.
            path: '/audit',
            lazy: () => import('~/routes/system-settings-redirect'),
          },
          {
            // FRONT-1818: the personal settings page. It was `/account` for
            // the three months FRONT-1263's merge held, and that direction is
            // inverted here — "Settings" already meant ORG Settings, so the
            // product shipped a Settings page that was not yours next to an
            // Account page that was your settings.
            path: '/settings',
            lazy: () => import('~/routes/settings'),
          },
          {
            // FRONT-1263 merged Profile, Settings and Security into one
            // destination; FRONT-1818 moved that destination to `/settings`.
            // All three older paths land on the matching section (the
            // destination is worked out in `lib/settings-sections.ts`) so
            // bookmarks and links in chat history keep working.
            path: '/account',
            lazy: () => import('~/routes/settings-redirect'),
          },
          {
            path: '/security',
            lazy: () => import('~/routes/settings-redirect'),
          },
          {
            path: '/profile',
            lazy: () => import('~/routes/settings-redirect'),
          },
          {
            // FRONT-2376: System Settings — the eight former sidebar pages
            // (Policy, Tools, Connections, Audit log, Org Settings, Sandbox,
            // Status, Diagnostics), addressed by `?section=`.
            path: '/system-settings',
            lazy: () => import('~/routes/system-settings'),
          },
          {
            // FRONT-2376: Diagnostics moved into the System Settings modal.
            path: '/diagnostics',
            lazy: () => import('~/routes/system-settings-redirect'),
          },
          {
            // FRONT-2376: Policy moved into the System Settings modal.
            path: '/policy',
            lazy: () => import('~/routes/system-settings-redirect'),
          },
          {
            // FRONT-2376: Org Settings moved into the System Settings modal.
            // The modal is itself the admin gate now — see
            // `system-settings-redirect.tsx`.
            path: '/org-settings',
            lazy: () => import('~/routes/system-settings-redirect'),
          },
          {
            // FRONT-2376: Status moved into the System Settings modal.
            path: '/status',
            lazy: () => import('~/routes/system-settings-redirect'),
          },
          {
            path: '/notifications',
            lazy: () => import('~/routes/notifications'),
          },
        ],
      },
    ],
  },
];
